import { useEffect, useState } from 'react';
import { Box, Txt } from './ui/primitives';
import { LoginPage } from './components/LoginPage';
import Dashboard from './Dashboard';
import { useAuth } from './hooks/useAuth';
import { StorageKeys, readStorage } from './utils/storage';

/**
 * Application root and authentication gate.
 *
 * `Dashboard` is only mounted once a session exists, so no warehouse data
 * request is ever made while signed out. While the initial session check is in
 * flight we show a neutral splash rather than flashing the login form at a user
 * who is already signed in.
 *
 * Theme is applied here as well as in the dashboard so the login screen honours
 * the user's light/dark preference.
 */
export default function App(): JSX.Element {
  const { user, checking, login, logout, onSessionExpired } = useAuth();

  const [theme] = useState<'light' | 'dark'>(() => {
    const stored = readStorage(StorageKeys.theme);
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  if (checking) {
    return (
      <Box className="min-h-screen grid place-items-center bg-surface-page">
        <Txt className="text-[13px] text-ink-muted">Loading…</Txt>
      </Box>
    );
  }

  if (!user) {
    return <LoginPage onLogin={login} />;
  }

  return <Dashboard user={user} onLogout={logout} onSessionExpired={onSessionExpired} />;
}
